import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { storage } from '@/services/storage';
import type { WeeklyReport } from '@/types/WeeklyReport';
import { addWeeks, format, startOfWeek, subWeeks } from 'date-fns';
import { AlertCircle, CalendarIcon, CheckCircle, PlusCircle, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UserComplianceTrackerProps {
    reports: WeeklyReport[];
    userEmail: string;
    onCreateReport?: (weekLabel: string) => void;
}

interface WeekData {
    startDate: Date;
    endDate: Date;
    label: string;
    key: string;
}

const UserComplianceTracker: React.FC<UserComplianceTrackerProps> = ({
    reports,
    userEmail,
    onCreateReport
}) => {
    const [weeksToShow, setWeeksToShow] = useState<number>(8);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = storage.getItem('from_compliance_tab');
        return savedTab || 'reports';
    });

    useEffect(() => {
        storage.setItem('from_compliance_tab', activeTab);
    }, [activeTab]);

    // Generate weeks data
    const weeks = useMemo(() => {
        const result: WeekData[] = [];
        const today = new Date();
        const baseDate = startDate || today;

        // Start from (weeksToShow - 1) weeks ago
        let currentDate = subWeeks(baseDate, weeksToShow - 1);

        for (let i = 0; i < weeksToShow; i++) {
            // Week starts on Sunday
            const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday

            // Week ends on Thursday (4 days after Sunday)
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 4); // Thursday (0 = Sunday, 4 = Thursday)

            // Set to end of day
            weekEnd.setHours(23, 59, 59, 999);

            const weekKey = format(weekStart, 'yyyy-MM-dd');

            result.push({
                startDate: weekStart,
                endDate: weekEnd,
                label: `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`,
                key: weekKey
            });

            currentDate = addWeeks(currentDate, 1);
        }

        return result;
    }, [weeksToShow, startDate]);

    // Calculate user's report compliance
    const complianceData = useMemo(() => {
        const userReports = reports.filter(report => report.createdBy === userEmail);
        console.log('Processing compliance for user:', userEmail);
        console.log('Total user reports:', userReports.length);
        console.log('Total weeks to check:', weeks.length);

        // Create a map of week keys to report status
        const weekMap = new Map<string, {
            hasSentReport: boolean;
            reportId?: string;
            report?: WeeklyReport;
        }>();

        // Initialize all weeks as missed
        weeks.forEach(week => {
            weekMap.set(week.key, { hasSentReport: false });
        });

        // Extract date from a string like "MMM d - MMM d, yyyy" or similar formats
        const extractDatesFromString = (dateString: string): { start: Date | null, end: Date | null } => {
            try {
                // Try different date formats
                // Format 1: "MMM d - MMM d, yyyy" (e.g., "Jan 1 - Jan 7, 2023")
                const pattern1 = /([A-Za-z]{3}\s+\d{1,2})\s*-\s*([A-Za-z]{3}\s+\d{1,2},\s*\d{4})/;
                // Format 2: "MMM d to MMM d, yyyy" (e.g., "Jan 1 to Jan 7, 2023")
                const pattern2 = /([A-Za-z]{3}\s+\d{1,2})\s*to\s*([A-Za-z]{3}\s+\d{1,2},\s*\d{4})/;

                const match = dateString.match(pattern1) || dateString.match(pattern2);

                if (match) {
                    const startDateStr = match[1];
                    const endDateStr = match[2];

                    // Extract year from end date
                    const yearMatch = endDateStr.match(/\d{4}/);
                    const year = yearMatch ? yearMatch[0] : new Date().getFullYear().toString();

                    // Add year to start date if it doesn't have one
                    const startDateWithYear = startDateStr.includes(year) ? startDateStr : `${startDateStr}, ${year}`;

                    const startDate = new Date(startDateWithYear);
                    const endDate = new Date(endDateStr);

                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                        return { start: startDate, end: endDate };
                    }
                }

                return { start: null, end: null };
            } catch (error) {
                console.error('Error extracting dates from string:', dateString, error);
                return { start: null, end: null };
            }
        };

        // Mark weeks with reports
        userReports.forEach(report => {
            if (!report.date) return;

            const reportDate = new Date(report.date);
            console.log('Processing report:', {
                id: report.id,
                subject: report.subject,
                date: format(reportDate, 'MMM d, yyyy'),
                dateRange: report.dateRange
            });

            // Try multiple approaches to match reports to weeks

            // Approach 1: Extract dates from subject
            let reportDates = { start: null, end: null };
            if (report.subject) {
                reportDates = extractDatesFromString(report.subject);
                if (reportDates.start && reportDates.end) {
                    console.log(`Extracted dates from subject: ${format(reportDates.start, 'MMM d, yyyy')} - ${format(reportDates.end, 'MMM d, yyyy')}`);
                }
            }

            // Approach 2: Extract dates from dateRange if approach 1 failed
            if ((!reportDates.start || !reportDates.end) && report.dateRange) {
                reportDates = extractDatesFromString(report.dateRange);
                if (reportDates.start && reportDates.end) {
                    console.log(`Extracted dates from dateRange: ${format(reportDates.start, 'MMM d, yyyy')} - ${format(reportDates.end, 'MMM d, yyyy')}`);
                }
            }

            // Match by extracted dates
            if (reportDates.start && reportDates.end) {
                weeks.forEach(week => {
                    // Check if there's any overlap between the report's date range and the week
                    const dateRangeOverlapsWeek = (
                        // Report starts during the week
                        (reportDates.start >= week.startDate && reportDates.start <= week.endDate) ||
                        // Report ends during the week
                        (reportDates.end >= week.startDate && reportDates.end <= week.endDate) ||
                        // Report spans the entire week
                        (reportDates.start <= week.startDate && reportDates.end >= week.endDate)
                    );

                    if (dateRangeOverlapsWeek) {
                        console.log(`Matched by extracted dates: Report ${report.id} matches week ${week.label}`);
                        weekMap.set(week.key, {
                            hasSentReport: true,
                            reportId: report.id,
                            report
                        });
                    }
                });
            }

            // Approach 3: Check if the week label is contained in the dateRange or subject
            weeks.forEach(week => {
                // Skip if already matched
                const existingMatch = weekMap.get(week.key);
                if (existingMatch && existingMatch.hasSentReport) {
                    return;
                }

                // Check dateRange
                if (report.dateRange && report.dateRange.includes(week.label)) {
                    console.log(`Matched by dateRange text: Report ${report.id} matches week ${week.label}`);
                    weekMap.set(week.key, {
                        hasSentReport: true,
                        reportId: report.id,
                        report
                    });
                    return;
                }

                // Check subject
                if (report.subject && report.subject.includes(week.label)) {
                    console.log(`Matched by subject text: Report ${report.id} matches week ${week.label}`);
                    weekMap.set(week.key, {
                        hasSentReport: true,
                        reportId: report.id,
                        report
                    });
                    return;
                }
            });

            // Approach 4: Fallback to creation date
            weeks.forEach(week => {
                // Skip if already matched
                const existingMatch = weekMap.get(week.key);
                if (existingMatch && existingMatch.hasSentReport) {
                    return;
                }

                // Check if the report creation date falls within the week
                if (reportDate >= week.startDate && reportDate <= week.endDate) {
                    console.log(`Matched by creation date: Report ${report.id} matches week ${week.label}`);
                    weekMap.set(week.key, {
                        hasSentReport: true,
                        reportId: report.id,
                        report
                    });
                }
            });
        });

        // Count missed weeks
        const missedWeeks = Array.from(weekMap.values()).filter(week => !week.hasSentReport).length;
        console.log('Compliance summary:', {
            totalWeeks: weeks.length,
            missedWeeks,
            complianceRate: ((weeks.length - missedWeeks) / weeks.length) * 100
        });

        return {
            weekMap,
            missedWeeks,
            totalWeeks: weeks.length,
            complianceRate: ((weeks.length - missedWeeks) / weeks.length) * 100
        };
    }, [reports, userEmail, weeks]);

    // Handle creating a new report for a specific week
    const handleCreateReport = (week: WeekData) => {
        // Store flag to indicate user came from compliance tab
        storage.setItem('from_compliance_tab', 'true');

        if (onCreateReport) {
            // Use the callback if provided
            onCreateReport(week.label);

            // Also store the actual date objects for more precise date setting
            const dateObj = {
                from: week.startDate,
                to: week.endDate
            };
            storage.setItem('report_date_objects', JSON.stringify(dateObj));
        } else {
            // Fallback to the previous approach
            storage.setItem('report_date_range', week.label);
            storage.setItem('report_date_objects', JSON.stringify({
                from: week.startDate,
                to: week.endDate
            }));
            storage.setItem('dashboard_active_tab', 'create');
            navigate('/dashboard');
        }
    };

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        storage.setItem('from_compliance_tab', 'true');
    };

    const handleWeekSelect = (week: any) => {
        const dateObj = {
            start: week.start,
            end: week.end
        };
        storage.setItem('report_date_objects', JSON.stringify(dateObj));
        storage.setItem('report_date_range', week.label);
        storage.setItem('report_date_objects', JSON.stringify({
            start: week.start,
            end: week.end
        }));
        storage.setItem('dashboard_active_tab', 'create');
    };

    return (
        <Card className="shadow-sm">
            <CardHeader>
                <CardTitle>Your Report Compliance</CardTitle>
                <CardDescription>Track your weekly report submissions</CardDescription>
            </CardHeader>

            <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium mb-1">Weeks to Show</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2"
                            value={weeksToShow}
                            onChange={(e) => setWeeksToShow(Number(e.target.value))}
                        >
                            <option value="4">Last 4 weeks</option>
                            <option value="8">Last 8 weeks</option>
                            <option value="12">Last 12 weeks</option>
                            <option value="26">Last 26 weeks</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Start From</label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-left font-normal"
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {startDate ? format(startDate, 'PPP') : <span>Current date</span>}
                                    {startDate && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="ml-auto h-6 w-6"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setStartDate(undefined);
                                            }}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar
                                    mode="single"
                                    selected={startDate}
                                    onSelect={setStartDate}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium">Compliance Summary</h3>
                        <Badge variant={complianceData.missedWeeks > 0 ? "outline" : "default"}>
                            {complianceData.complianceRate.toFixed(0)}% Complete
                        </Badge>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                            className="bg-primary h-2.5 rounded-full"
                            style={{ width: `${complianceData.complianceRate}%` }}
                        ></div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                        You&apos;ve submitted {complianceData.totalWeeks - complianceData.missedWeeks} out of {complianceData.totalWeeks} reports
                        {complianceData.missedWeeks > 0 && ` (${complianceData.missedWeeks} missing)`}
                    </p>
                </div>

                <div className="space-y-4">
                    <h3 className="text-lg font-medium">Weekly Report Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {weeks.map((week) => {
                            const weekData = complianceData.weekMap.get(week.key);
                            const hasSentReport = weekData?.hasSentReport || false;

                            return (
                                <div
                                    key={week.key}
                                    className={`p-3 rounded-lg border flex items-center justify-between ${hasSentReport ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                                        }`}
                                >
                                    <div>
                                        <div className="font-medium">{week.label}</div>
                                        <div className="text-sm text-muted-foreground flex items-center mt-1">
                                            {hasSentReport ? (
                                                <>
                                                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                                                    Report submitted
                                                </>
                                            ) : (
                                                <>
                                                    <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                                                    Report missing
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {!hasSentReport && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-1"
                                            onClick={() => handleCreateReport(week)}
                                        >
                                            <PlusCircle className="h-4 w-4" />
                                            Create
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </CardContent>

            <CardFooter className="border-t pt-4">
                <div className="text-sm text-muted-foreground">
                    <p>Reports are expected to be submitted weekly by Thursday.</p>
                </div>
            </CardFooter>
        </Card>
    );
};

export default UserComplianceTracker;